/** Keep in sync with JobSearchPage keyword limits. */
const MAX_KEYWORD_PILLS = 5;

function parseKeywordSegments(serialized) {
  return String(serialized || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_KEYWORD_PILLS);
}

function normalizeWorkModeParam(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "remote" || s === "hybrid" || s === "onsite") return s;
  return "";
}

/**
 * @param {string} search — `location.search` (with or without leading `?`)
 * @returns {object} Partial fields compatible with JobSearchPage `initialSearchState` / criteria state
 */
export function parseJobSearchUrlFragment(search) {
  const q = String(search || "").trim();
  const normalized = q.startsWith("?") ? q.slice(1) : q;
  if (!normalized) return {};
  const params = new URLSearchParams(normalized);

  const countriesRaw = params.get("countries") || "";
  const criteriaCountryCodes = countriesRaw
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter((c) => /^[A-Z]{2}$/.test(c));

  const criteriaTitles = String(params.get("keywords") || params.get("q") || "").trim();
  const criteriaWorkMode = normalizeWorkModeParam(params.get("workMode"));
  const cur = String(params.get("currency") || "").trim().toUpperCase();
  const criteriaSalaryCurrency = cur || "GBP";
  const salaryMin = String(params.get("salaryMin") ?? params.get("min") ?? "").trim();
  const salaryMax = String(params.get("salaryMax") ?? params.get("max") ?? "").trim();

  const out = {
    criteriaCountryCodes,
    criteriaTitles,
    criteriaWorkMode,
    criteriaSalaryCurrency,
    salaryMin,
    salaryMax,
  };

  if (params.get("notForMe") === "1") out.showNotForMe = true;
  if (params.get("applied") === "1") out.showApplied = true;
  if (params.has("saved")) out.showSaved = params.get("saved") !== "0";
  if (params.get("view") === "tab") out.resultsView = "tab";

  return out;
}

export function jobSearchUrlHasRunnableCriteria(fragment) {
  if (!fragment || typeof fragment !== "object") return false;
  const codes = fragment.criteriaCountryCodes;
  if (!Array.isArray(codes) || codes.length === 0) return false;
  return parseKeywordSegments(fragment.criteriaTitles).length > 0;
}

/**
 * Normalized query string (no leading `?`) for the runnable criteria encoded in `location.search`.
 * Use this to compare with `jobSearchUrlSyncedRef` so encoding/spacing differences do not skip or re-fire searches.
 * @param {string} search
 * @returns {string | null}
 */
export function canonicalJobSearchQueryStringFromLocationSearch(search) {
  const frag = parseJobSearchUrlFragment(search);
  if (!jobSearchUrlHasRunnableCriteria(frag)) return null;
  return buildJobSearchParamsFromState({
    criteriaCountryCodes: frag.criteriaCountryCodes || [],
    criteriaTitles: frag.criteriaTitles || "",
    criteriaWorkMode: frag.criteriaWorkMode || "",
    criteriaSalaryCurrency: frag.criteriaSalaryCurrency || "GBP",
    salaryMin: frag.salaryMin ?? "",
    salaryMax: frag.salaryMax ?? "",
    showNotForMe: frag.showNotForMe !== undefined ? Boolean(frag.showNotForMe) : true,
    showApplied: frag.showApplied !== undefined ? Boolean(frag.showApplied) : true,
    showSaved: frag.showSaved !== undefined ? frag.showSaved : true,
    resultsView: frag.resultsView === "tab" ? "tab" : "list",
  }).toString();
}

export function jobSearchUrlHasAnyCriteria(fragment) {
  if (!fragment || typeof fragment !== "object") return false;
  if (Array.isArray(fragment.criteriaCountryCodes) && fragment.criteriaCountryCodes.length > 0) return true;
  if (parseKeywordSegments(fragment.criteriaTitles).length > 0) return true;
  if (String(fragment.criteriaWorkMode || "").trim()) return true;
  if (String(fragment.salaryMin || "").trim() || String(fragment.salaryMax || "").trim()) return true;
  return false;
}

/**
 * @param {object} state — criteria + optional filters/view
 * @returns {URLSearchParams}
 */
export function buildJobSearchParamsFromState(state) {
  const {
    criteriaCountryCodes = [],
    criteriaTitles = "",
    criteriaWorkMode = "",
    criteriaSalaryCurrency = "GBP",
    salaryMin = "",
    salaryMax = "",
    showNotForMe = true,
    showApplied = true,
    showSaved = true,
    resultsView = "list",
  } = state || {};

  const p = new URLSearchParams();
  const codes = (criteriaCountryCodes || [])
    .map((c) => String(c || "").trim().toUpperCase())
    .filter((c) => /^[A-Z]{2}$/.test(c));
  if (codes.length) p.set("countries", codes.join(","));

  const kw = parseKeywordSegments(criteriaTitles);
  if (kw.length) p.set("keywords", kw.join(", "));

  const wm = normalizeWorkModeParam(criteriaWorkMode);
  if (wm) p.set("workMode", wm);

  const cur = String(criteriaSalaryCurrency || "GBP").trim().toUpperCase();
  if (cur) p.set("currency", cur);

  const smin = String(salaryMin ?? "").trim();
  const smax = String(salaryMax ?? "").trim();
  if (smin) p.set("salaryMin", smin);
  if (smax) p.set("salaryMax", smax);

  if (showNotForMe) p.set("notForMe", "1");
  if (showApplied) p.set("applied", "1");
  if (!showSaved) p.set("saved", "0");
  if (resultsView === "tab") p.set("view", "tab");

  return p;
}
